/**
 * Self check-in del ALUMNO (F3-A): input grande del código de 6 caracteres (el
 * "QR sin cámara" resuelve al MISMO código) → POST /api/attendance/checkin. El
 * servidor valida código + sesión abierta + matrícula activa + ventana ±30 min.
 * Estados premium de éxito/error.
 */
import { type Component, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, QrCode, XCircle } from "lucide-solid";
import { handleApiResponse } from "@space/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

type Result =
  | { kind: "idle" }
  | { kind: "success"; status: string }
  | { kind: "error"; message: string };

export const CheckinView: Component = () => {
  const { t } = useI18n();
  const [code, setCode] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [result, setResult] = createSignal<Result>({ kind: "idle" });

  const successLate = () => {
    const r = result();
    return r.kind === "success" && r.status === "late";
  };
  const errorMessage = () => {
    const r = result();
    return r.kind === "error" ? r.message : "";
  };

  const submit = async () => {
    const value = code().trim().toUpperCase();
    if (value.length < 4) {
      return;
    }
    setBusy(true);
    setResult({ kind: "idle" });
    try {
      const res = await handleApiResponse(
        api.POST("/api/attendance/checkin", { body: { code: value } }),
      );
      setResult({ kind: "success", status: res?.status ?? "present" });
      setCode("");
    } catch {
      setResult({
        kind: "error",
        message: t(msg`Invalid code, closed check-in, or you are outside the time window.`),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="mx-auto flex max-w-md flex-col items-center gap-6 p-6" data-testid="checkin-view">
      <div class="flex flex-col items-center gap-2 text-center">
        <QrCode class="h-10 w-10 text-primary" />
        <h1 class="flex items-center gap-1 font-display text-2xl font-extrabold">
          {t(msg`Check in`)}
        </h1>
        <p class="text-sm text-muted-foreground">
          {t(msg`Enter the code your teacher is showing on screen.`)}
        </p>
      </div>

      <input
        type="text"
        inputmode="text"
        autocomplete="off"
        maxlength="6"
        data-testid="checkin-code-input"
        value={code()}
        onInput={(e) => setCode(e.currentTarget.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void submit();
          }
        }}
        placeholder="ABC123"
        class="w-full rounded-2xl border-2 border-border bg-field-background px-4 py-4 text-center font-mono text-4xl font-extrabold uppercase tracking-[0.4em] focus-visible:border-primary focus-visible:outline-none"
      />

      <Button
        data-testid="checkin-submit"
        onClick={() => void submit()}
        disabled={busy() || code().trim().length < 4}
        class="w-full"
      >
        {busy() ? t(msg`Checking in…`) : t(msg`Check in`)}
      </Button>

      <Show when={result().kind === "success"}>
        <div
          class="flex w-full flex-col items-center gap-2 rounded-2xl bg-success/10 p-6 text-center"
          data-testid="checkin-success"
        >
          <CheckCircle2 class="h-12 w-12 text-success" />
          <p class="text-lg font-bold text-success">{t(msg`You're checked in!`)}</p>
          <Show when={successLate()}>
            <p class="text-sm text-muted-foreground">{t(msg`Marked as late.`)}</p>
          </Show>
        </div>
      </Show>

      <Show when={result().kind === "error"}>
        <div
          class="flex w-full flex-col items-center gap-2 rounded-2xl bg-danger/10 p-6 text-center"
          data-testid="checkin-error"
        >
          <XCircle class="h-12 w-12 text-danger" />
          <p class="text-sm font-semibold text-danger">{errorMessage()}</p>
        </div>
      </Show>
    </div>
  );
};
